import React, { useEffect, useMemo, useRef, useState } from "react";
import { buildEtvDemoUrl } from "../../features/demo/demoContract";

const DemoEtvFrame = ({
  session,
  sessionId,
  markRendered,
  baseUrl = process.env.REACT_APP_ETV_DEMO_URL || "https://etikaf-tv-demo.web.app",
  timeoutMs = 12_000,
}) => {
  const iframeRef = useRef(null);
  const shellRef = useRef(null);
  const acceptedBuildRef = useRef("");
  const pendingRenderedRef = useRef(null);
  const [frameKey, setFrameKey] = useState(0);
  const [frameState, setFrameState] = useState("loading");
  const expectedOrigin = useMemo(() => new URL(baseUrl).origin, [baseUrl]);
  const src = useMemo(
    () => buildEtvDemoUrl({ baseUrl, sessionId }),
    [baseUrl, sessionId]
  );
  const expectedRevision = session?.delivery?.revision;
  const expectedContentType = session?.delivery?.contentType;

  useEffect(() => {
    acceptedBuildRef.current = "";
    pendingRenderedRef.current = null;
  }, [frameKey, sessionId]);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return undefined;
    const applyScale = () => {
      shell.style.setProperty("--demo-etv-scale", String(shell.clientWidth / 1920));
    };
    applyScale();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", applyScale);
      return () => window.removeEventListener("resize", applyScale);
    }
    const observer = new ResizeObserver(applyScale);
    observer.observe(shell);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setFrameState("loading");
    const timeout = window.setTimeout(
      () => setFrameState((current) => current === "loading" ? "timeout" : current),
      timeoutMs
    );
    return () => window.clearTimeout(timeout);
  }, [expectedContentType, expectedRevision, frameKey, sessionId, timeoutMs]);

  useEffect(() => {
    const receive = (event) => {
      const payload = event.data || {};
      if (event.origin !== expectedOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (payload.source !== "etikaf-etv-demo" || payload.sessionId !== sessionId) return;

      const currentDelivery =
        payload.revision === expectedRevision && payload.contentType === expectedContentType;
      const validBuild = typeof payload.buildId === "string" && /^[0-9a-f]{7,40}$/i.test(payload.buildId);

      if (payload.type === "ready" && validBuild) {
        acceptedBuildRef.current = payload.buildId;
        setFrameState("ready");
        return;
      }
      if (payload.type === "error") {
        setFrameState("error");
        return;
      }
      if (
        payload.type === "rendered" &&
        Number.isSafeInteger(expectedRevision) &&
        typeof expectedContentType === "string" &&
        validBuild &&
        acceptedBuildRef.current === payload.buildId
      ) {
        if (!currentDelivery) {
          pendingRenderedRef.current = payload;
          return;
        }
        pendingRenderedRef.current = null;
        setFrameState("rendered");
        markRendered("etv", expectedRevision, expectedContentType);
      }
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [expectedContentType, expectedOrigin, expectedRevision, markRendered, sessionId]);

  useEffect(() => {
    const pending = pendingRenderedRef.current;
    if (
      !pending
      || pending.revision !== expectedRevision
      || pending.contentType !== expectedContentType
      || pending.buildId !== acceptedBuildRef.current
    ) return;
    pendingRenderedRef.current = null;
    setFrameState("rendered");
    markRendered("etv", expectedRevision, expectedContentType);
  }, [expectedContentType, expectedRevision, markRendered]);

  return (
    <section className="demo-preview demo-etv-preview" aria-label="Paparan ETV demo">
      <div className="demo-panel-heading">
        <div>
          <p className="demo-eyebrow">ETIKAF TV</p>
          <h2>Paparan skrin masjid</h2>
        </div>
        <span className={`demo-frame-status is-${frameState}`} aria-live="polite">
          {frameState === "rendered" ? "Diterima" : frameState === "ready" ? "Sedia" : "Demo"}
        </span>
      </div>
      <div className="demo-tv-shell" ref={shellRef}>
        <iframe
          key={frameKey}
          ref={iframeRef}
          src={src}
          title="ETV demo"
          allow="autoplay"
          width="1920"
          height="1080"
          data-native-viewport="1920x1080"
        />
      </div>
      {(frameState === "timeout" || frameState === "error") && (
        <div className="demo-frame-help" role="status">
          <p>Paparan ETV mengambil masa lebih lama daripada biasa. Makluman anda masih disimpan.</p>
          <button type="button" onClick={() => setFrameKey((value) => value + 1)}>Cuba semula</button>
        </div>
      )}
    </section>
  );
};

export default DemoEtvFrame;
